import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cc_admin_token";

const fieldClass =
  "w-full rounded-none border-0 border-b border-black/25 bg-transparent px-0 py-4 text-base text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors duration-300 focus:border-primary";

const formatError = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong.";
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (t = token) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${t}` } });
      setEnquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setEnquiries(null);
      } else {
        toast.error("Could not load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
  };

  if (!token) {
    return (
      <main className="mx-auto flex min-h-screen max-w-[1600px] items-center px-6 sm:px-12" data-testid="admin-login-page">
        <Reveal className="w-full max-w-md">
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-primary">Admin</p>
          <h1 className="font-syne text-4xl font-extrabold uppercase tracking-wide">Enquiry Desk</h1>
          <form onSubmit={login} className="mt-12 space-y-10" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@creativecurve.com" className={fieldClass} />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Password</label>
              <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={fieldClass} />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-button" className="w-full bg-primary px-10 py-4 font-syne text-sm font-bold uppercase tracking-[0.15em] text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background disabled:opacity-50">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </Reveal>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen max-w-[1600px] px-6 pb-24 pt-40 sm:px-12" data-testid="admin-dashboard">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-primary">Admin</p>
          <h1 className="font-syne text-4xl font-extrabold uppercase tracking-wide sm:text-5xl">Enquiries</h1>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="admin-enquiry-count">
            {enquiries ? `${enquiries.length} enquir${enquiries.length === 1 ? "y" : "ies"} received` : "Loading..."}
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => load()} data-testid="admin-refresh-button" className="flex items-center gap-2 border border-black/25 px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:border-foreground">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border border-primary/50 px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-primary transition-colors duration-300 hover:bg-primary hover:text-primary-foreground">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-12 border-t border-black/10" data-testid="admin-enquiries-list">
        {loading && !enquiries && <p className="py-16 text-sm text-muted-foreground">Loading enquiries...</p>}
        {enquiries && enquiries.length === 0 && (
          <div className="flex flex-col items-start gap-4 py-16" data-testid="admin-empty-state">
            <Inbox className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No enquiries yet. New submissions from the contact page will appear here.</p>
          </div>
        )}
        {enquiries?.map((q) => (
          <div key={q.id} className="grid grid-cols-1 gap-4 border-b border-black/10 py-8 sm:grid-cols-12 sm:gap-8" data-testid={`admin-enquiry-${q.id}`}>
            <div className="sm:col-span-3">
              <p className="font-syne text-lg font-bold uppercase tracking-wide">{q.name}</p>
              <a href={`mailto:${q.email}`} className="text-sm text-primary hover:underline">{q.email}</a>
              {q.organisation && <p className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">{q.organisation}</p>}
            </div>
            <div className="sm:col-span-2">
              {q.interest && <span className="border border-black/15 px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{q.interest}</span>}
            </div>
            <p className="text-sm leading-relaxed text-foreground/80 sm:col-span-5">{q.message}</p>
            <p className="text-xs text-muted-foreground sm:col-span-2 sm:text-right">{new Date(q.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>
    </main>
  );
}
